import { useRouter } from 'next/router'
import { useState } from 'react'

import { Button } from '@wartek-id/button-v1'
import { Text } from '@wartek-id/text-v1'
import { useToast } from '@wartek-id/toast'
import { Icon } from '@wartek-id/icon-v2'

import PanelPerilakuKerja from 'app/KinerjaPage/components/PanelPerilakuKerja'
import useContentContext from 'app/KinerjaPage/hooks/useContentContext'
import DialogSignSKP from './DialogSignSKP'
import {
  useAPIDeleteKinerja,
  useAPIUpdateState,
} from 'api/kinerja/useAPIKinerja'
import useSKP from 'app/KinerjaPage/hooks/useSKP'
import useSKPTrackEvent from 'app/KinerjaPage/hooks/useSKPTrackEvent'
import StickyFooter from 'app/KinerjaPage/components/StickyFooter'
import DialogDeleteSKP from './DialogDeleteSKP'
import { PENGELOLAAN_KINERJA_GURU_PATH } from 'app/KinerjaShared/constants/path'

import type { Kinerja } from 'types/kinerja'

import IconBookRaporGraphicDoc from 'assets/icons/img-book-rapor-graphic-doc.svg'

interface TabPerilakuKerjaProps {
  skp: Kinerja
  isDeleteSKPEnabled?: boolean
}

const TabPerilakuKerjaApproved = ({
  skp,
  isDeleteSKPEnabled = false,
}: TabPerilakuKerjaProps) => {
  const router = useRouter()
  const toast = useToast()
  const { trackEvent } = useSKPTrackEvent()
  const { getAspekPerilakuKerjaById } = useContentContext()

  const { mutate: signSKP, isLoading: isSigningSKP } = useAPIUpdateState()
  const { mutate: deleteSKP, isLoading: isDeletingSKP } = useAPIDeleteKinerja()

  const [isOpenDialogSignSKP, setIsOpenDialogSignSKP] = useState(false)
  const [isOpenDialogDeleteSKP, setIsOpenDialogDeleteSKP] = useState(false)

  const { getChangesPerilakuKerja } = useSKP(skp)

  const handleClickApprove = () => {
    setIsOpenDialogSignSKP(true)
  }

  const handleClickConfirm = () => {
    trackEvent({
      event_name: 'CLICK_KINERJA_PLANNING_AGREEMENT',
      screen_name: 'KINERJA_PLANNING_AGREEMENT_POP_UP',
      kinerja_planning_id: skp?.id,
      kinerja_planning_status: skp?.status,
    })

    signSKP(
      {
        skpId: skp.id,
      },
      {
        onError() {
          toast({
            message: 'Gagal menyepakati SKP',
          })
        },
        onSuccess() {
          router.push(PENGELOLAAN_KINERJA_GURU_PATH)
        },
      }
    )
  }

  const handleClickDelete = () => {
    deleteSKP(
      {},
      {
        onError() {
          toast({
            message: 'Perencanaan gagal dihapus. Coba lagi.',
          })
        },
        onSuccess() {
          setIsOpenDialogDeleteSKP(false)
          toast({
            icon: <Icon color="inverse">check</Icon>,
            message: 'Perencanaan berhasil dihapus!',
          })
          router.push(PENGELOLAAN_KINERJA_GURU_PATH)
        },
      }
    )
  }

  return (
    <>
      <div className="p-9">
        <div className="flex flex-col gap-9">
          {skp.status === 'APPROVED' && (
            <div className="flex gap-3">
              <IconBookRaporGraphicDoc className="w-16 h-16 shrink-0" />
              <div>
                <Text variant="display-sm">
                  Cek Perencanaan Kinerja yang disetujui atasan
                </Text>
                <Text className="mt-1.5">
                  Kata “Ada penyesuaian” artinya atasan mengubah Rencana Hasil
                  Kerja Anda. Mohon perhatikan dan lanjut sepakati, ya.
                </Text>
              </div>
            </div>
          )}

          {skp.reviewPrilakuKerja.map((perilakuKerja) => {
            const aspek = getAspekPerilakuKerjaById(perilakuKerja.aspekId)
            const {
              originalIndikator,
              indikator,
              originalPerwujudanPerilaku,
              perwujudanPerilaku,
            } = getChangesPerilakuKerja(perilakuKerja)

            return (
              <PanelPerilakuKerja
                key={`PanelPerilakuKerja-${aspek.id}`}
                data-testid={`PanelPerilakuKerja-${aspek.id}`}
                title={aspek?.value}
                originalIndikatorPerilaku={originalIndikator}
                indikatorPerilaku={indikator}
                originalPerwujudanPerilaku={originalPerwujudanPerilaku}
                perwujudanPerilaku={perwujudanPerilaku}
              />
            )
          })}
        </div>
      </div>

      <StickyFooter>
        {skp.status === 'APPROVED' ? (
          <Button
            size="lg"
            onClick={() => handleClickApprove()}
            type="button"
            data-testid="btn-back-to-home"
          >
            Sepakati
          </Button>
        ) : (
          <Button
            size="lg"
            onClick={() => {
              router.push(PENGELOLAAN_KINERJA_GURU_PATH)
            }}
            type="button"
            data-testid="btn-back-to-home"
          >
            Kembali ke Beranda
          </Button>
        )}
      </StickyFooter>

      <DialogSignSKP
        data-testid="dialog-sign-skp"
        isDeleteSKPEnabled={isDeleteSKPEnabled}
        isOpen={isOpenDialogSignSKP}
        onClose={() => setIsOpenDialogSignSKP(false)}
        isLoading={isSigningSKP}
        onClickCancel={() => {
          setIsOpenDialogSignSKP(false)

          if (isDeleteSKPEnabled) {
            setIsOpenDialogDeleteSKP(true)
          }
        }}
        onClickConfirm={handleClickConfirm}
      />

      <DialogDeleteSKP
        data-testid="dialog-delete-skp"
        isOpen={isOpenDialogDeleteSKP}
        onClose={() => setIsOpenDialogDeleteSKP(false)}
        isLoading={isDeletingSKP}
        onClickCancel={() => {
          setIsOpenDialogDeleteSKP(false)
          setIsOpenDialogSignSKP(true)
        }}
        onClickConfirm={handleClickDelete}
      />
    </>
  )
}

export default TabPerilakuKerjaApproved
